import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  /** Width and any other sizing; the default suits a short form. */
  readonly className?: string;
  readonly children: ReactNode;
}

/**
 * A native `<dialog>` opened with `showModal`: the browser supplies the backdrop, the focus
 * trap and Escape-to-close, which a hand-rolled overlay has to reimplement and usually
 * gets partly wrong.
 *
 * The content mounts only while open, so anything it holds in state — a form's draft — starts
 * afresh on every opening rather than where it was abandoned last time.
 */
export function Modal({ open, title, onClose, className, children }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) {
      return;
    }
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label={title}
      onClose={onClose}
      onClick={(event) => {
        // A click on the backdrop lands on the dialog element itself, never on its content.
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      className={cn(
        'm-auto flex max-h-[min(85dvh,720px)] w-[min(360px,calc(100vw-32px))] flex-col rounded-lg border border-line bg-surface p-0 text-ink shadow-xl backdrop:bg-black/50 [&:not([open])]:hidden',
        className,
      )}
    >
      {open && (
        <>
          <header className="flex shrink-0 items-center justify-between border-b border-line px-3 py-2">
            <h2 className="text-sm font-semibold">{title}</h2>
            <button type="button" aria-label="Close" onClick={onClose} className="rounded p-0.5 text-muted hover:text-ink">
              <X className="size-4" />
            </button>
          </header>
          {children}
        </>
      )}
    </dialog>
  );
}
